import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import RefundReasonBadge from "@/components/feature/RefundReasonBadge";

function mapTxStatus(type: string | null, status: string | null): string {
  const t = (type || "").toLowerCase();
  const s = (status || "").toLowerCase();

  if (t === "charge") {
    if (s === "pending") return "대기";
    if (s === "rejected") return "거절";
    if (s === "approved") return "충전완료";
    return "대기";
  }
  if (t === "use") return "사용완료";
  if (t === "bonus") return "보너스";
  if (t === "admin_grant") return "관리자지급";
  if (t === "admin_deduct") return "관리자차감";
  if (t === "refund") return "환불";
  return "완료";
}

function mapTxMethod(pm: string | null, type: string | null): string {
  if (!pm) {
    const t = (type || "").toLowerCase();
    if (t === "admin_grant" || t === "admin_deduct" || t === "adjust" || t === "deduct" || t === "credit_adjust") {
      return "관리자 조정";
    }
    return "기타";
  }
  const m = pm.toLowerCase();
  if (m.includes("card") || m.includes("stripe")) return "카드";
  if (m.includes("bank") || m.includes("transfer") || m.includes("무통장") || m.includes("계좌")) return "무통장/계좌이체";
  if (m.includes("kakao")) return "카카오페이";
  if (m.includes("toss")) return "토스페이";
  if (m.includes("naver")) return "네이버페이";
  return pm;
}

const txStatusStyles: Record<string, string> = {
  완료: "bg-sky-500/20 text-sky-400 border-sky-500/30",
  대기: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  거절: "bg-red-500/20 text-red-400 border-red-500/30",
  충전완료: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  환불: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  사용완료: "bg-sky-500/20 text-sky-400 border-sky-500/30",
  보너스: "bg-violet-500/20 text-violet-400 border-violet-500/30",
  관리자지급: "bg-teal-500/20 text-teal-400 border-teal-500/30",
  관리자차감: "bg-orange-500/20 text-orange-400 border-orange-500/30",
};

function formatDateTime(createdAt: string): { date: string; time: string } {
  const d = new Date(createdAt);
  const date = d
    .toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" })
    .replace(/\. /g, ".")
    .replace(/\.$/, "");
  const time = d.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit", hour12: false });
  return { date, time };
}

interface UserCreditHistoryProps {
  userId: string;
}

export default function UserCreditHistoryPanel({ userId }: UserCreditHistoryProps) {
  const [items, setItems] = useState<Array<{
    id: string;
    type: string | null;
    amount: number;
    balance_after: number;
    description: string | null;
    refund_reason: string | null;
    payment_method: string | null;
    created_at: string;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("로그인이 필요합니다.");

      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-admin-credit-transactions?user_id=${encodeURIComponent(userId)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      const txs = (data.transactions ?? []) as Array<{
        id: string;
        type: string | null;
        amount: number;
        balance_after: number;
        description: string | null;
        refund_reason: string | null;
        payment_method: string | null;
        created_at: string;
      }>;
      setItems(txs);
    } catch (err: any) {
      setError(err.message || "데이터를 불러오는 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-8 h-8 border-2 border-rose-500/40 border-t-rose-500 rounded-full animate-spin mb-3"></div>
        <p className="text-gray-500 text-sm">크레딧 내역 불러오는 중...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <i className="ri-error-warning-line text-3xl text-red-400 mb-2 block"></i>
        <p className="text-red-400 text-sm mb-3">{error}</p>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-white/[0.06] hover:bg-white/[0.1] text-gray-300 rounded-lg text-sm cursor-pointer transition-colors"
        >
          다시 시도
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/[0.04] mb-3">
          <i className="ri-coin-line text-2xl text-gray-600"></i>
        </div>
        <p className="text-gray-500 text-sm font-medium">크레딧 내역 없음</p>
        <p className="text-gray-700 text-xs mt-1">해당 회원의 충전/차감 이력이 없습니다</p>
      </div>
    );
  }

  const totalAdjust = items.reduce((sum, it) => sum + it.amount, 0);
  const totalDeposit = items.filter((it) => it.amount > 0).reduce((sum, it) => sum + it.amount, 0);
  const totalDeduct = items.filter((it) => it.amount < 0).reduce((sum, it) => sum + it.amount, 0);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-3 text-center">
          <p className="text-emerald-400 text-lg font-bold">₩{totalDeposit.toLocaleString()}</p>
          <p className="text-gray-500 text-xs mt-0.5">총 충전</p>
        </div>
        <div className="bg-rose-500/5 border border-rose-500/20 rounded-xl p-3 text-center">
          <p className="text-rose-400 text-lg font-bold">₩{Math.abs(totalDeduct).toLocaleString()}</p>
          <p className="text-gray-500 text-xs mt-0.5">총 차감</p>
        </div>
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-3 text-center">
          <p className="text-white text-lg font-bold">₩{totalAdjust.toLocaleString()}</p>
          <p className="text-gray-500 text-xs mt-0.5">순 변동</p>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2 max-h-72 overflow-y-auto pr-0.5">
        {items.map((it) => {
          const status = mapTxStatus(it.type, it.status);
          const method = mapTxMethod(it.payment_method, it.type);
          const { date, time } = formatDateTime(it.created_at);
          const isPositive = it.amount > 0;

          return (
            <div key={it.id} className="flex items-center gap-3 bg-white/[0.02] border border-white/[0.06] rounded-xl px-3 py-3">
              <div className={`w-9 h-9 flex items-center justify-center rounded-xl shrink-0 ${isPositive ? "bg-emerald-500/10" : "bg-rose-500/10"}`}>
                <i className={`${isPositive ? "ri-arrow-up-line text-emerald-400" : "ri-arrow-down-line text-rose-400"} text-base`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-sm font-bold whitespace-nowrap ${isPositive ? "text-emerald-400" : "text-rose-400"}`}>
                    {isPositive ? "+" : ""}₩{it.amount.toLocaleString()}
                  </p>
                  <span className={`px-1.5 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${txStatusStyles[status]}`}>
                    {status}
                  </span>
                  {it.type === "refund" && (
                    <RefundReasonBadge reason={it.refund_reason} description={it.description} />
                  )}
                  {it.payment_method && (
                    <span className="text-gray-500 text-[11px] whitespace-nowrap">{method}</span>
                  )}
                </div>
                <p className="text-gray-500 text-xs mt-0.5 truncate">{it.description || "-"}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className="text-gray-600 text-[11px]">{date} {time}</p>
                  <p className="text-gray-700 text-[11px]">잔액 ₩{it.balance_after.toLocaleString()}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}